/*
  Plain, translated texts for common server errors (plan, stage 5). The server
  answers in short English with field names ("cloaking_safe_url must be a valid
  http(s) URL"); the interface shows a sentence in the client's language
  instead. The plain English text is the translation key (./i18n/errorTexts.js).
  Unknown errors pass through unchanged.
*/
import { TRANSLATION_MAPS } from '../i18n';

const EXACT = {
  "Campaign not found":
    "This campaign was not found. It may have been deleted, or it belongs to another workspace.",
  "Offer not found":
    "This offer was not found. It may have been deleted.",
  "Domain not found":
    "This domain was not found. It may have been deleted.",
  "Landing page not found":
    "This landing page was not found. It may have been deleted.",
  "Traffic source not found":
    "This traffic source was not found. It may have been deleted.",
  "Affiliate network not found":
    "This affiliate network was not found. It may have been deleted.",
  "Stream not found":
    "This stream was not found. It may have been deleted.",
  "Domain does not belong to this workspace":
    "This domain belongs to another workspace. Choose one of your own domains.",
  "cloaking_safe_url must be a valid http(s) URL":
    "The safe page address must be a full link starting with http:// or https://.",
  "url is required":
    "Enter the address (URL).",
  "Enter a full http(s) URL":
    "Enter a full link starting with http:// or https://.",
  "ids must be a non-empty list":
    "Select at least one item.",
  "Invalid ZIP file":
    "This file is not a valid ZIP archive. Pack the files into a .zip and upload it again.",
  "White pages must be static (.html/.css/.js) — .php is not allowed":
    "A white page must be static files (.html, .css, .js); .php files are not allowed.",
  "Landing page storage limit exceeded":
    "There is no more room for landing files. Delete landings you do not use.",
  "Offer storage limit exceeded":
    "There is no more room for offer files. Delete offers you do not use.",
  "Cannot delete traffic source: it's used in campaigns":
    "This traffic source is used in campaigns. Change the source in those campaigns first.",
  "Cannot delete affiliate network: it's linked to offers":
    "This affiliate network is linked to offers. Unlink it from those offers first.",
  "Invalid hostname":
    "This is not a valid domain name. Enter it like track.example.com, without http:// and without a path.",
  "This hostname is reserved":
    "This domain name is reserved by the tracker. Use a domain of your own.",
  "Domain already registered":
    "This domain has already been added, in your workspace or another one.",
  "Domain must be verified first":
    "Verify the domain first: add the TXT record and press Verify.",
  "Domain pool has no healthy verified domains":
    "The domain pool has no verified working domains. Add or restore a domain in the pool.",
  "Invalid email address":
    "This e-mail address is not valid.",
  "start must be before end":
    "The start date must be before the end date.",
  "Dates must be YYYY-MM-DD":
    "Enter dates as YYYY-MM-DD.",
  "Invalid plan":
    "This plan does not exist. Reload the page and choose a plan again.",
  "Subscription required: your trial has ended or the subscription is inactive. Open Billing to choose a plan.":
    "No active subscription: campaigns are turned off and new items cannot be created. Open Billing to choose a plan.",
};

// Things a plan counts, as the server names them in quota answers ({what} below).
// Translated like any other key (./i18n/planLimits.js).
export const PLAN_NOUNS = ['automation rules', 'scheduled reports', 'public report links'];

// The server says "upgrade to the a higher plan" when no plan has the feature.
const upgradeTo = (needed, withPlan, withoutPlan) => (needed === 'a higher' ? withoutPlan : withPlan);

/*
  [pattern, key] or [pattern, (match) => [key, vars]]. Keys keep {placeholders};
  they are filled after translation. {what} is a PLAN_NOUNS entry and is
  translated too. Plan answers (402) come from server.py:
  _billing_require_feature, _billing_require_quota (limit 0 and limit reached),
  _billing_require_member_slot, _billing_require_domain_slot. The server uses
  an em dash in some and a hyphen in others, so both are accepted.
*/
const PATTERNS = [
  [new RegExp("^Unknown or foreign ids in .+: use offers and pages of this workspace$"), "Some selected offers or pages belong to another workspace. Choose ones from this workspace."],
  [
    /^The (.+?) plan does not include this feature\s*[—–-]\s*upgrade to the (.+?) plan or higher in Billing\.$/,
    (m) => [
      upgradeTo(
        m[2],
        'The {plan} plan does not include this feature. Upgrade to the {needed} plan or higher in Billing.',
        'The {plan} plan does not include this feature. Upgrade to a higher plan in Billing.'
      ),
      { plan: m[1], needed: m[2] },
    ],
  ],
  [
    /^The (.+?) plan does not include (.+?)\s*[—–-]\s*upgrade to the (.+?) plan or higher in Billing\.$/,
    (m) => [
      upgradeTo(
        m[3],
        'The {plan} plan does not include {what}. Upgrade to the {needed} plan or higher in Billing.',
        'The {plan} plan does not include {what}. Upgrade to a higher plan in Billing.'
      ),
      { plan: m[1], what: m[2], needed: m[3] },
    ],
  ],
  [
    /^The (.+?) plan includes (\d+) (.+?) and you are using (\d+)\. Upgrade in Billing to add more\.$/,
    (m) => [
      '{plan} plan limit: {what} — {limit}, you already have {used}. Upgrade in Billing to add more.',
      { plan: m[1], limit: m[2], what: m[3], used: m[4] },
    ],
  ],
  [
    /^The (.+?) plan includes (\d+) users?\s*[—–-]\s*upgrade in Billing to invite more team members\.$/,
    (m) => ['{plan} plan limit: users — {limit}. Upgrade in Billing to invite more team members.', { plan: m[1], limit: m[2] }],
  ],
  [
    /^The (.+?) plan includes (\d+) custom domains?\s*[—–-]\s*upgrade in Billing to add more\.$/,
    (m) => ['{plan} plan limit: custom domains — {limit}. Upgrade in Billing to add more.', { plan: m[1], limit: m[2] }],
  ],
];

function fill(template, vars) {
  return template.replace(/\{(\w+)\}/g, (all, name) => (vars && vars[name] != null ? String(vars[name]) : all));
}

function currentLocale() {
  try {
    const saved = window.localStorage.getItem('locale');
    if (saved) return saved;
    return String(window.navigator.language || 'en').slice(0, 2).toLowerCase();
  } catch (_e) {
    return 'en';
  }
}

/** {key, vars} for a known server error, or null. */
export function matchApiError(detail) {
  if (typeof detail !== 'string') return null;
  const text = detail.trim();
  if (Object.prototype.hasOwnProperty.call(EXACT, text)) return { key: EXACT[text], vars: null };
  for (const [re, target] of PATTERNS) {
    const m = text.match(re);
    if (!m) continue;
    if (typeof target === 'function') {
      const [key, vars] = target(m);
      return { key, vars };
    }
    return { key: target, vars: null };
  }
  return null;
}

/** The plain English text for a server error, or null when unknown. */
export function plainErrorKey(detail) {
  const hit = matchApiError(detail);
  return hit ? fill(hit.key, hit.vars) : null;
}

/** The plain text in the client's language, or null when unknown. */
export function translateApiError(detail, locale = currentLocale()) {
  const hit = matchApiError(detail);
  if (!hit) return null;
  const map = TRANSLATION_MAPS[locale] || {};
  const vars = hit.vars ? { ...hit.vars } : null;
  if (vars && vars.what) vars.what = map[vars.what] || vars.what;
  return fill(map[hit.key] || hit.key, vars);
}
